import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtDays } from "../lib/format";
import { UserCircle, Envelope, BellRinging, Newspaper, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [prefs, setPrefs] = useState({ alertes: false, bulletin: false });
  const [emailStatus, setEmailStatus] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    api.get("/analyses").then((r) => setAnalyses(r.data));
    api.get("/email/status").then((r) => setEmailStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.email_preferences) setPrefs(user.email_preferences);
  }, [user]);

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSavingPrefs(true);
    try {
      await api.patch("/auth/me/email-preferences", next);
      toast.success(`Préférence « ${key} » ${next[key] ? "activée" : "désactivée"}`);
      if (refreshUser) refreshUser();
    } catch {
      setPrefs(prefs);
      toast.error("Erreur enregistrement");
    } finally {
      setSavingPrefs(false);
    }
  };

  const sendTestBulletin = async () => {
    setSendingTest(true);
    try {
      const r = await api.post("/email/bulletin/send-me");
      const s = r.data?.status;
      if (s === "sent") toast.success(`Bulletin envoyé à ${user?.email}`);
      else if (s === "dry_run") toast.info("Mode dry-run · email loggé côté serveur");
      else toast.error(r.data?.error?.slice(0, 180) || "Échec envoi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec envoi");
    } finally {
      setSendingTest(false);
    }
  };

  const totalSacrifie = analyses.reduce((acc, a) => acc + (a.resultat?.vitalist?.V1 || 0), 0);
  const moyenneSrv = analyses.length === 0 ? 0 : analyses.reduce((acc, a) => acc + (a.resultat?.vitalist?.V5?.srv_global || 0), 0) / analyses.length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <div className="mb-10 fade-up">
        <div className="label-mono">§ 06 · Vita-Mea</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Profil citoyen</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-black/15 border border-black/15 mb-8">
        <div className="bg-[var(--paper)] p-8 lg:col-span-1 flex flex-col items-center text-center">
          <UserCircle size={84} weight="duotone" className="text-[var(--signal)]" />
          <h2 className="mt-4 font-serif text-3xl" data-testid="profile-name">{user?.nom_complet}</h2>
          <div className="label-mono mt-2">{user?.profession || "—"}</div>
          <div className="font-mono text-xs text-[var(--muted)] mt-1">{user?.pays}</div>
          <div className="mt-6 border-t border-black/15 pt-4 w-full font-mono text-xs text-[var(--muted)]">
            <div className="flex justify-between"><span>Email</span><span>{user?.email}</span></div>
            <div className="flex justify-between mt-1"><span>Dossier ouvert</span><span>{fmtDate(user?.cree_le)}</span></div>
          </div>
        </div>

        <div className="bg-[var(--paper)] p-8 lg:col-span-2">
          <div className="label-mono">Bilan vitaliste cumulé</div>
          <h2 className="font-serif text-3xl mt-1">Mon souverain temporel</h2>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-black/15 border border-black/15">
            <div className="bg-[var(--paper)] p-5">
              <div className="label-mono">Analyses produites</div>
              <div className="mt-2 text-4xl font-light tabular-nums" data-testid="profile-stat-analyses">{analyses.length}</div>
            </div>
            <div className="bg-[var(--paper)] p-5">
              <div className="label-mono">Total V1 cumulé</div>
              <div className="mt-2 text-4xl font-light tabular-nums">{fmtDays(totalSacrifie, 1)}</div>
            </div>
            <div className="bg-[var(--paper)] p-5">
              <div className="label-mono">SRV moyen</div>
              <div className="mt-2 text-4xl font-light tabular-nums">{moyenneSrv.toFixed(1)}</div>
            </div>
          </div>

          <div className="mt-8 p-6 border border-[var(--signal)] bg-[var(--signal-light)]">
            <div className="label-mono text-[var(--signal-hover)]">Manifeste personnel</div>
            <p className="mt-3 font-serif italic text-lg leading-relaxed">
              « Je, {user?.nom_complet}, citoyen{user?.profession ? ` ${user.profession}` : ""} de {user?.pays || "République"},
              déclare avoir mesuré ma souveraineté vitaliste et engage ma responsabilité juridique
              à exercer mon droit de reddition de comptes. »
            </p>
          </div>
        </div>
      </div>

      <div className="border border-black/15 bg-[var(--paper)] mb-8" data-testid="profile-email-prefs">
        <div className="border-b border-black/15 p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Envelope size={22} className="text-[var(--signal)]" />
            <div>
              <div className="font-serif text-2xl">Notifications email</div>
              <div className="font-mono text-xs text-[var(--muted)]">Resend.com · {emailStatus?.live ? "service actif" : "mode dry-run (logs serveur)"}</div>
            </div>
          </div>
          <button onClick={sendTestBulletin} disabled={sendingTest} data-testid="profile-send-test-bulletin"
            className="border border-black px-4 py-2 hover:bg-black hover:text-[var(--paper)] label-mono flex items-center gap-2 disabled:opacity-50">
            <PaperPlaneTilt size={14} /> {sendingTest ? "Envoi..." : "Envoyer un test à mon email"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15">
          <PrefRow
            icon={BellRinging}
            label="Alertes ponctuelles"
            description="Email à chaque fin d'analyse Observatory, détection de passifs occultes (Debt-Pro), ou argumentaire juridique généré."
            active={prefs.alertes}
            onToggle={() => togglePref("alertes")}
            disabled={savingPrefs}
            testid="pref-alertes"
          />
          <PrefRow
            icon={Newspaper}
            label="Bulletin hebdomadaire"
            description="Digest des nouveautés Observatory + tendances 2026, chaque lundi matin (09:00 UTC)."
            active={prefs.bulletin}
            onToggle={() => togglePref("bulletin")}
            disabled={savingPrefs}
            testid="pref-bulletin"
          />
        </div>

        {emailStatus && !emailStatus.live && (
          <div className="border-t border-black/15 p-4 font-mono text-xs text-[var(--muted)]">
            Mode dry-run actif : les emails sont écrits dans les logs serveur sans envoi réel. Configurez <code>RESEND_API_KEY</code> côté backend pour activer l'envoi.
          </div>
        )}
        {emailStatus?.live && (
          <div className="border-t border-black/15 p-4 font-mono text-xs text-[var(--muted)]">
            Expéditeur : <strong>{emailStatus.sender_name}</strong> &lt;{emailStatus.sender_email}&gt;. En mode test Resend, seul l'email du propriétaire de la clé reçoit les envois (vérifiez votre domaine sur resend.com/domains pour diffuser à tous les inscrits).
          </div>
        )}
      </div>

      <div className="border border-black/15 bg-[var(--paper)] p-6">
        <div className="label-mono">§ Historique condensé</div>
        <table className="w-full mt-4 text-sm">
          <thead className="label-mono text-left border-b border-black/15">
            <tr>
              <th className="p-3">Date</th>
              <th className="p-3">Libellé</th>
              <th className="p-3 text-right">V1</th>
              <th className="p-3 text-right">D2</th>
              <th className="p-3 text-right">Niveau</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {analyses.map((a) => (
              <tr key={a.id} className="hover:bg-[var(--paper-2)]">
                <td className="p-3 font-mono text-xs">{fmtDate(a.cree_le)}</td>
                <td className="p-3 font-serif">{a.libelle}</td>
                <td className="p-3 text-right tabular-nums">{fmtDays(a.resultat?.vitalist?.V1 || 0, 1)}</td>
                <td className="p-3 text-right tabular-nums">{fmtDays(a.resultat?.dette?.D2 || 0, 1)}</td>
                <td className="p-3 text-right">
                  <span className="font-mono text-xs px-2 py-0.5" style={{ background: a.resultat?.synthese?.qualification?.couleur, color: "#fff" }}>
                    {a.resultat?.synthese?.qualification?.niveau}
                  </span>
                </td>
              </tr>
            ))}
            {analyses.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center label-mono text-[var(--muted)]">Aucune analyse</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PrefRow({ icon: Icon, label, description, active, onToggle, disabled, testid }) {
  return (
    <div className="bg-[var(--paper)] p-5" data-testid={testid}>
      <div className="flex items-start gap-4">
        <Icon size={22} weight={active ? "fill" : "regular"} className={active ? "text-[var(--signal)]" : "text-[var(--muted)]"} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-serif text-xl">{label}</div>
            <button
              onClick={onToggle}
              disabled={disabled}
              data-testid={`${testid}-toggle`}
              aria-pressed={active}
              className={`relative inline-flex items-center h-7 w-14 border-2 transition-all disabled:opacity-50 ${
                active ? "bg-[var(--signal)] border-[var(--signal)]" : "bg-[var(--paper)] border-black/30"
              }`}
            >
              <span className={`absolute top-[2px] h-[18px] w-[18px] bg-[var(--paper)] border border-black transition-transform ${active ? "translate-x-[28px]" : "translate-x-[2px]"}`} />
              <span className="sr-only">{active ? "désactiver" : "activer"}</span>
            </button>
          </div>
          <p className="mt-2 text-sm text-[var(--muted)] leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}
